import { API_BASE_URL } from '$lib/config';
import { getAccessToken, refreshSession, setAccessToken } from './auth';

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined)
    }
  });

  if (response.status === 401 && !retried && !endpoint.startsWith('/auth/')) {
    try {
      await refreshSession();
      return apiFetch<T>(endpoint, options, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  if (response.status === 204) return null as T;
  return (await response.json()) as T;
}

// Binary POSTs share apiFetch's token refresh/retry and error contract.
export async function apiBlob(endpoint: string, options: RequestInit, retried = false): Promise<Blob> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include', ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(options.headers as Record<string, string> | undefined) }
  });
  if (response.status === 401 && !retried) {
    try { await refreshSession(); return apiBlob(endpoint, options, true); }
    catch { setAccessToken(null); throw new Error('Authentication required. Please log in again.'); }
  }
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    throw new Error(typeof data === 'object' && data !== null && 'detail' in data ? String(data.detail) : `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.blob();
}

// uploadFile POSTs a single file as multipart/form-data (field name "file")
// — apiFetch always forces a `Content-Type: application/json` header, which
// would stomp the multipart boundary fetch sets automatically for a
// FormData body, so uploads get their own thin wrapper instead of reusing it.
// `fields` are extra plain form fields sent alongside the file (e.g. the CSV
// importer's `force` conflict-override flag).
export async function uploadFile<T>(
  endpoint: string,
  file: File,
  fields?: Record<string, string>
): Promise<T> {
  const token = getAccessToken();
  const form = new FormData();
  for (const [key, value] of Object.entries(fields ?? {})) form.append(key, value);
  form.append('file', file);
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      typeof data === 'object' && data !== null && 'detail' in data ? String((data as { detail?: unknown }).detail) : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return data as T;
}

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'An unexpected error occurred.');
}

// downloadFile fetches a binary response (the PDF endpoints) and saves it
// via a throwaway <a download> — apiFetch above always parses JSON, so a
// PDF byte stream needs its own fetch, not a reuse of apiFetch's body
// handling. extraHeaders lets callers attach e.g. X-Share-Password for the
// public share PDF route (see PublicShareView.svelte).
export async function downloadFile(
  endpoint: string,
  extraHeaders: Record<string, string> = {},
  fallbackFilename = 'quotation.pdf'
): Promise<void> {
  const response = await fetchOk(endpoint, extraHeaders);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// openFileInNewTab fetches a binary response and opens it in a new browser
// tab, for "Preview" controls (?inline=1 PDF routes) — a plain <a href>
// can't carry the Authorization header this API needs, so it goes through
// the same authenticated fetch path as downloadFile, then window.open()s the
// resulting object URL instead of triggering a download.
export async function openFileInNewTab(endpoint: string, extraHeaders: Record<string, string> = {}): Promise<void> {
  const response = await fetchOk(endpoint, extraHeaders);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Deliberately not revoking the object URL here — the new tab needs it to
  // stay alive for as long as the user has that tab open, unlike the
  // throwaway <a> in downloadFile above.
}

async function fetchOk(endpoint: string, extraHeaders: Record<string, string>): Promise<Response> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders
    }
  });
  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response;
}
